import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, Text, TouchableOpacity, TextInput, ScrollView,
  ActivityIndicator, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  getFirestore, collection, getDocs, getDoc, setDoc, query, orderBy,
  serverTimestamp, writeBatch, deleteField, doc, arrayUnion, where, runTransaction,
} from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../components/LoginPersistence';
import {
  MERCH_PRESETS, MERCH_DEFAULT_HARD_BAND_PCT, MERCH_DEFAULT_SOFT_BAND_PCT, MERCH_ENTRY_FIELDS,
  calcMerch, merchPresetFor, merchSummary, merchByBand, isMerchEntry,
} from '../utils/expenseMerch';
import {
  EXPENSE_CATEGORIES, INCOME_CATEGORIES, UNIT_MODES, SCOPES,
  toDayKey, addDaysToKey, tourDayKeys, lineTotal, unitLabel, categoryLabel, typeForCategory,
  entryTitle, entryNote, newSeriesId, plannedDays, scopeTargets,
  addSubheader, renameSubheader, removeSubheader, subheadersFor, subheaderName, movePatch,
  entryFlows, overviewTotals, groupEntries, groupByDay, chunkArray, seriesRateLabel,
  cleanMoneyText, draftTotals, entriesForTour, pickDefaultTour, tourEntryDates,
  CUSTOM_CATEGORY_FIELDS, customCategoryId, cleanCustomCategories, categoryDeletePlan, typedNameMatches,
} from '../utils/expenseItems';
import { useBudgetRevisions } from '../contexts/FeatureFlagsContext';
import { tourDayKinds } from '../utils/tourDayKinds';
import { loadV2Prefs, V2_PREFS_EVENT, hexWithAlpha } from '../utils/calendarV2Prefs';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const numericText = cleanMoneyText;
const toNum = (v) => { const x = parseFloat(cleanMoneyText(v)); return Number.isFinite(x) ? x : 0; };
const signedUsd = (n) => `${n < 0 ? '−' : '+'}${usd(Math.abs(n))}`;
const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const todayKey = () => toDayKey(new Date());
const lineId = () => `pl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

// Budget dates always read in English (Trev 2026-10-28), whatever the browser's language.
const DATE_LOCALE = 'en-US';
const formatDay = (key, withYear = true) => {
  const k = toDayKey(key);
  if (!k) return 'No date';
  const [y, m, d] = k.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(DATE_LOCALE, {
    weekday: 'short', month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}),
  });
};

const newLine = (unitMode = 'one_off') => ({ id: lineId(), name: '', unitMode, amount: '', unitCount: '', unitPrice: '', description: '', descOpen: false });

const lineHasContent = (l) => !!(String(l.name || '').trim() || toNum(l.amount) > 0 || toNum(l.unitCount) > 0 || toNum(l.unitPrice) > 0 || String(l.description || '').trim());

const emptyLine = () => ({ id: lineId(), name: '', unitMode: 'one_off', amount: '', unitCount: '', unitPrice: '', note: '' });

// ─── Small building blocks ───────────────────────────────────────────────────

function Label({ ui, children, style }) {
  return <Text style={[ui.label, style]}>{children}</Text>;
}

function Pill({ ui, label, on, onPress, icon, dashed, tone, small, accessibilityLabel }) {
  const accent = tone || '#6c5ce7';
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel={accessibilityLabel || label}
      style={{
        paddingVertical: small ? 5 : 8, paddingHorizontal: small ? 11 : 14, borderRadius: 18,
        backgroundColor: on ? accent : (dashed ? 'transparent' : ui.surface),
        borderWidth: 1, borderStyle: dashed ? 'dashed' : 'solid',
        borderColor: on ? accent : (dashed ? accent : ui.border),
        flexDirection: 'row', alignItems: 'center', gap: 5,
      }}
    >
      {icon ? <Ionicons name={icon} size={13} color={on ? '#fff' : (dashed ? accent : ui.textSecondary)} /> : null}
      <Text style={{ fontSize: small ? 12 : 13, fontWeight: '600', color: on ? '#fff' : (dashed ? accent : ui.textSecondary) }}>{label}</Text>
    </TouchableOpacity>
  );
}

function Card({ ui, children, style, accent }) {
  return (
    <View style={[{
      backgroundColor: ui.card, borderRadius: 14, borderWidth: 1, borderColor: ui.border, padding: 14, gap: 12,
    }, accent ? { borderLeftWidth: 3, borderLeftColor: accent } : null, style]}>
      {children}
    </View>
  );
}

function Field({ ui, value, onChangeText, placeholder, numeric, multiline, style, autoFocus, onSubmitEditing }) {
  return (
    <TextInput
      style={[ui.input, multiline ? { minHeight: 72, textAlignVertical: 'top' } : null, style]}
      value={value}
      onChangeText={numeric ? (t) => onChangeText(numericText(t)) : onChangeText}
      placeholder={placeholder}
      placeholderTextColor={ui.placeholder}
      keyboardType={numeric ? 'decimal-pad' : 'default'}
      multiline={multiline}
      autoFocus={autoFocus}
      onSubmitEditing={onSubmitEditing}
    />
  );
}

// A short text box that opens from a "+ New …" pill and hands the typed name back.
function InlinePillAdd({ ui, label, placeholder, onAdd }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const commit = () => {
    const clean = text.trim();
    if (clean) onAdd(clean);
    setText('');
    setOpen(false);
  };
  if (!open) return <Pill ui={ui} label={label} icon="add" dashed onPress={() => setOpen(true)} />;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexBasis: '100%' }}>
      <TextInput
        autoFocus
        style={[ui.input, { flex: 1, paddingVertical: 8 }]}
        value={text}
        onChangeText={setText}
        placeholder={placeholder}
        placeholderTextColor={ui.placeholder}
        onSubmitEditing={commit}
      />
      <TouchableOpacity onPress={commit} style={{ paddingHorizontal: 8 }}>
        <Text style={{ color: '#6c5ce7', fontWeight: '700', fontSize: 13 }}>Add</Text>
      </TouchableOpacity>
      <TouchableOpacity onPress={() => { setText(''); setOpen(false); }} style={{ paddingHorizontal: 4 }}>
        <Text style={{ color: ui.textSecondary, fontSize: 13 }}>Cancel</Text>
      </TouchableOpacity>
    </View>
  );
}

// Compact one-row date picker: step a day either way, tap the date for the native calendar, or jump to today.
function DateField({ ui, label, value, onChange, accent }) {
  const inputRef = useRef(null);
  const key = toDayKey(value) || todayKey();
  const isToday = key === todayKey();
  const shift = (n) => onChange(addDaysToKey(key, n));
  const openPicker = () => {
    const el = inputRef.current;
    if (el && typeof el.showPicker === 'function') { try { el.showPicker(); } catch (_) {} }
  };
  const stepStyle = {
    width: 34, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center',
    backgroundColor: ui.surface, borderWidth: 1, borderColor: ui.border,
  };
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      {label ? <Text style={[ui.label, { width: 44 }]} numberOfLines={1}>{label}</Text> : null}
      <TouchableOpacity onPress={() => shift(-1)} accessibilityLabel="Previous day" style={stepStyle}>
        <Ionicons name="chevron-back" size={16} color={ui.textSecondary} />
      </TouchableOpacity>
      <View style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <TouchableOpacity
          onPress={openPicker}
          accessibilityLabel={`${label || 'Date'}: ${formatDay(key)}`}
          style={[stepStyle, { width: '100%', flexDirection: 'row', gap: 6, paddingHorizontal: 10, justifyContent: 'flex-start' },
            accent ? { borderColor: accent, borderWidth: 2 } : null]}
        >
          <Ionicons name="calendar-outline" size={15} color="#6c5ce7" />
          <Text style={{ fontSize: 13, fontWeight: '600', color: ui.textPrimary, flexShrink: 1 }} numberOfLines={1}>{formatDay(key, false)}</Text>
        </TouchableOpacity>
          {Platform.OS === 'web'
            ? React.createElement('input', {
              ref: inputRef,
              type: 'date',
              value: key,
              onChange: (e) => { if (e.target.value) onChange(e.target.value); },
              onClick: openPicker,
              tabIndex: -1,
              'aria-hidden': true,
              style: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', colorScheme: ui.dark ? 'dark' : 'light' },
            })
            : null}
      </View>
      <TouchableOpacity onPress={() => shift(1)} accessibilityLabel="Next day" style={stepStyle}>
        <Ionicons name="chevron-forward" size={16} color={ui.textSecondary} />
      </TouchableOpacity>
      <Pill ui={ui} small label="Today" on={isToday} onPress={() => onChange(todayKey())} />
    </View>
  );
}

const DAY_KIND_NAMES = { show: 'Show day', travel: 'Travel day', off: 'Day off' };

// Revised: the tour's days in a row, tinted with the calendar's own Show / Travel / Off colors.
function TourDayStrip({ ui, days, kinds, colors, value, onPick }) {
  const selected = toDayKey(value);
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6, paddingVertical: 2 }}>
      {days.map((key) => {
        const kind = kinds[key] || 'off';
        const color = colors[kind] || ui.border;
        const on = key === selected;
        const [yy, mm, dd] = key.split('-').map(Number);
        const d = new Date(yy, mm - 1, dd);
        return (
          <TouchableOpacity
            key={key}
            onPress={() => onPick(key)}
            accessibilityLabel={`${formatDay(key)}, ${DAY_KIND_NAMES[kind] || 'Tour day'}`}
            style={{
              width: 46, paddingVertical: 6, borderRadius: 10, alignItems: 'center',
              backgroundColor: `${color}${on ? '55' : '22'}`,
              borderWidth: on ? 2 : 1, borderColor: on ? color : `${color}88`,
            }}
          >
            <Text style={{ fontSize: 10, fontWeight: '700', color: ui.textSecondary, textTransform: 'uppercase' }}>
              {d.toLocaleDateString(DATE_LOCALE, { weekday: 'short' })}
            </Text>
            <Text style={{ fontSize: 15, fontWeight: '800', color: ui.textPrimary }}>{d.getDate()}</Text>
            <Text style={{ fontSize: 9, fontWeight: '600', color: ui.textSecondary }}>{d.toLocaleDateString(DATE_LOCALE, { month: 'short' })}</Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
}

function DayKindLegend({ ui, colors }) {
  const items = [['show', 'Show'], ['travel', 'Travel'], ['off', 'Off'], [null, 'Not a tour day']];
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
      {items.map(([kind, label]) => (
        <View key={label} style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
          <View style={{
            width: 10, height: 10, borderRadius: 3,
            backgroundColor: kind ? colors[kind] : 'transparent', borderWidth: kind ? 0 : 1, borderColor: ui.border,
          }} />
          <Text style={{ fontSize: 11, fontWeight: '600', color: ui.textSecondary }}>{label}</Text>
        </View>
      ))}
    </View>
  );
}

// One full-width dashed button that sits under the last line of a list.
function AddRowButton({ ui, label, onPress }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel={label}
      style={{
        width: '100%', minHeight: 46, borderRadius: 12, borderWidth: 1.5, borderStyle: 'dashed', borderColor: '#6c5ce7',
        flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
    >
      <Ionicons name="add" size={17} color="#6c5ce7" />
      <Text style={{ fontSize: 14, fontWeight: '700', color: '#6c5ce7' }}>{label}</Text>
    </TouchableOpacity>
  );
}

// Band/venue split: preset pills plus a "+ Custom" pill to type your own, e.g. 50/50 for a poster.
function SplitPicker({ label, value, onChange, customOpen, onCustomOpenChange, fallback, ui }) {
  const preset = merchPresetFor(value);
  const raw = String(value == null ? '' : value).trim();
  const band = parseFloat(raw);
  const bandOk = Number.isFinite(band) && band >= 0 && band <= 100;
  const hint = raw === '' || !Number.isFinite(band)
    ? `Blank, so the default ${fallback}/${100 - fallback} is used`
    : band > 100 ? 'Over 100, counted as 100' : null;
  return (
    <View style={{ gap: 8 }}>
      <Label ui={ui}>{label}</Label>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {MERCH_PRESETS.map((p) => (
          <Pill key={p.label} ui={ui} small label={p.label} on={!customOpen && !!preset && preset.band === p.band}
            onPress={() => { onChange(String(p.band)); onCustomOpenChange(false); }} />
        ))}
        <Pill ui={ui} small label="Custom" icon="add" dashed={!customOpen} on={customOpen}
          accessibilityLabel={`Custom ${label}`} onPress={() => onCustomOpenChange(true)} />
      </View>
      {customOpen ? (
        <Field ui={ui} numeric value={String(value ?? '')} onChangeText={onChange} placeholder="Band % you keep (e.g. 50)" />
      ) : null}
      {hint ? (
        <Text style={{ fontSize: 12, color: '#e67e22' }}>{hint}</Text>
      ) : bandOk ? (
        <Text style={{ fontSize: 12, color: ui.textSecondary }}>
          Band keeps {band}% · Venue keeps {Math.round((100 - band) * 100) / 100}%
        </Text>
      ) : null}
    </View>
  );
}

function MoneyLine({ label, value, color, bold, ui }) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', paddingVertical: 3 }}>
      <Text style={{ fontSize: bold ? 13 : 12, fontWeight: bold ? '700' : '500', color: ui.textSecondary }}>{label}</Text>
      <Text style={{ fontSize: bold ? 16 : 13, fontWeight: bold ? '800' : '600', color: color || ui.textPrimary }}>{value}</Text>
    </View>
  );
}

// Unit choice: one-off amount, per-day amount, or # of units x price. Merch lines skip per day.
function UnitFields({ ui, mode, onMode, amount, onAmount, unitCount, onUnitCount, unitPrice, onUnitPrice, modes, lockModes }) {
  const total = mode === 'units' ? toNum(unitCount) * toNum(unitPrice) : toNum(amount);
  return (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {(modes || UNIT_MODES).map((u) => (
          <Pill key={u.id} ui={ui} small label={u.label} on={mode === u.id} onPress={() => { if (!lockModes) onMode(u.id); }} />
        ))}
      </View>
      {!mode ? (
        <Text style={{ fontSize: 12, color: ui.textSecondary }}>Pick how this is counted to enter the amount.</Text>
      ) : mode === 'units' ? (
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <View style={{ flex: 1, gap: 6 }}>
            <Label ui={ui}># of units</Label>
            <Field ui={ui} numeric value={unitCount} onChangeText={onUnitCount} placeholder="0" />
          </View>
          <View style={{ flex: 1, gap: 6 }}>
            <Label ui={ui}>Price each $</Label>
            <Field ui={ui} numeric value={unitPrice} onChangeText={onUnitPrice} placeholder="0.00" />
          </View>
        </View>
      ) : (
        <View style={{ gap: 6 }}>
          <Label ui={ui}>{mode === 'per_day' ? 'Amount per day $' : 'Amount $'}</Label>
          <Field ui={ui} numeric value={amount} onChangeText={onAmount} placeholder="0.00" />
        </View>
      )}
      {mode === 'units' && total > 0 ? (
        <Text style={{ fontSize: 12, color: ui.textSecondary }}>{toNum(unitCount)} x {usd(toNum(unitPrice))} = {usd(total)}</Text>
      ) : null}
    </View>
  );
}

// Merch production cost: any number of line items, each with a name, a unit choice, and a note.
function ProductionLines({ ui, lines, onChange }) {
  const [openNotes, setOpenNotes] = useState({});
  const patch = (id, next) => onChange(lines.map((l) => (l.id === id ? { ...l, ...next } : l)));
  return (
    <View style={{ gap: 10 }}>
      {lines.length === 0 ? (
        <Text style={{ fontSize: 12, color: ui.textSecondary }}>Add what it cost to make the merch, one line per thing (shirts, printing, shipping).</Text>
      ) : null}
      {lines.map((l) => (
        <View key={l.id} style={{ backgroundColor: ui.surface, borderRadius: 12, padding: 12, gap: 10, borderWidth: 1, borderColor: ui.border }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Field ui={ui} value={l.name} onChangeText={(t) => patch(l.id, { name: t })} placeholder="Item name (e.g. Shirt printing)" style={{ flex: 1 }} />
            <TouchableOpacity onPress={() => onChange(lines.filter((x) => x.id !== l.id))} accessibilityLabel="Remove production line" style={{ padding: 8 }}>
              <Ionicons name="trash-outline" size={16} color="rgba(231,76,60,0.75)" />
            </TouchableOpacity>
          </View>
          <UnitFields
            ui={ui} modes={UNIT_MODES.filter((u) => u.id !== 'per_day')}
            mode={l.unitMode} onMode={(m) => patch(l.id, { unitMode: m })}
            amount={l.amount} onAmount={(v) => patch(l.id, { amount: v })}
            unitCount={l.unitCount} onUnitCount={(v) => patch(l.id, { unitCount: v })}
            unitPrice={l.unitPrice} onUnitPrice={(v) => patch(l.id, { unitPrice: v })}
          />
          {openNotes[l.id] || l.note ? (
            <Field ui={ui} multiline value={l.note} onChangeText={(t) => patch(l.id, { note: t })} placeholder="Description (optional)" />
          ) : (
            <TouchableOpacity onPress={() => setOpenNotes((p) => ({ ...p, [l.id]: true }))}>
              <Text style={{ fontSize: 12, fontWeight: '600', color: '#6c5ce7' }}>+ Add description</Text>
            </TouchableOpacity>
          )}
        </View>
      ))}
      <AddRowButton ui={ui} label="Add production cost line" onPress={() => onChange([...lines, emptyLine()])} />
    </View>
  );
}

// A centered choice box over the rail. (The built-in Alert has no multi-choice on web.)
// Second confirmation for deleting a category that still has entries: type its name to enable Delete.
function TypeToConfirmDialog({ ui, title, body, expected, confirmLabel, busy, onConfirm, onCancel }) {
  const [typed, setTyped] = useState('');
  const ok = typedNameMatches(typed, expected) && !busy;
  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, backgroundColor: 'rgba(10,10,20,0.62)', justifyContent: 'center', padding: 20 }}>
      <View style={{ backgroundColor: ui.bg, borderRadius: 16, borderWidth: 1, borderColor: ui.border, padding: 18, gap: 12 }}>
        <Text style={{ fontSize: 16, fontWeight: '800', color: ui.textPrimary }}>{title}</Text>
        {body ? <Text style={{ fontSize: 13, color: ui.textSecondary, lineHeight: 18 }}>{body}</Text> : null}
        <Text style={{ fontSize: 12, fontWeight: '700', color: ui.textPrimary }}>Type “{expected}” to confirm</Text>
        <TextInput
          autoFocus
          value={typed}
          onChangeText={setTyped}
          placeholder={expected}
          placeholderTextColor={ui.placeholder}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel={`Type ${expected} to confirm`}
          onSubmitEditing={() => { if (ok) onConfirm(); }}
          style={ui.input}
        />
        <TouchableOpacity
          onPress={() => { if (ok) onConfirm(); }}
          disabled={!ok}
          accessibilityState={{ disabled: !ok }}
          style={{ paddingVertical: 12, borderRadius: 12, alignItems: 'center', backgroundColor: ok ? '#e74c3c' : 'rgba(231,76,60,0.14)', borderWidth: 1, borderColor: 'rgba(231,76,60,0.4)', opacity: ok ? 1 : 0.6 }}
        >
          <Text style={{ fontSize: 14, fontWeight: '700', color: ok ? '#fff' : '#e74c3c' }}>{busy ? 'Deleting…' : confirmLabel}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onCancel} style={{ paddingVertical: 8, alignItems: 'center' }}>
          <Text style={{ fontSize: 14, fontWeight: '600', color: ui.textSecondary }}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function ChoiceDialog({ ui, title, body, options, onPick, onCancel }) {
  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, backgroundColor: 'rgba(10,10,20,0.62)', justifyContent: 'center', padding: 20 }}>
      <View style={{ backgroundColor: ui.bg, borderRadius: 16, borderWidth: 1, borderColor: ui.border, padding: 18, gap: 12 }}>
        <Text style={{ fontSize: 16, fontWeight: '800', color: ui.textPrimary }}>{title}</Text>
        {body ? <Text style={{ fontSize: 13, color: ui.textSecondary, lineHeight: 18 }}>{body}</Text> : null}
        <View style={{ gap: 8 }}>
          {options.map((o) => (
            <TouchableOpacity
              key={o.id}
              onPress={() => onPick(o.id)}
              style={{ paddingVertical: 12, paddingHorizontal: 14, borderRadius: 12, backgroundColor: o.danger ? 'rgba(231,76,60,0.14)' : ui.surface, borderWidth: 1, borderColor: o.danger ? 'rgba(231,76,60,0.4)' : ui.border }}
            >
              <Text style={{ fontSize: 14, fontWeight: '600', color: o.danger ? '#e74c3c' : ui.textPrimary }}>{o.label}</Text>
              {o.hint ? <Text style={{ fontSize: 12, color: ui.textSecondary, marginTop: 2 }}>{o.hint}</Text> : null}
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity onPress={onCancel} style={{ paddingVertical: 8, alignItems: 'center' }}>
          <Text style={{ fontSize: 14, fontWeight: '600', color: ui.textSecondary }}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

// Pick a category and subheader to move an item into. Merch stays in Merch; other items stay put by type.
function MoveSheet({ ui, entry, subheaders, onPick, onCancel, onCreate, customCategories }) {
  const isMerch = isMerchEntry(entry);
  const type = entry.type === 'income' || entry.type === 'expense' ? entry.type : typeForCategory(entry.category);
  const cats = [...(type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES), ...((customCategories || {})[type] || [])]
    .filter((c) => (isMerch ? c.id === 'merch' : c.id !== 'merch'));
  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, backgroundColor: 'rgba(10,10,20,0.62)', justifyContent: 'center', padding: 16 }}>
      <View style={{ backgroundColor: ui.bg, borderRadius: 16, borderWidth: 1, borderColor: ui.border, maxHeight: '88%' }}>
        <View style={{ padding: 18, paddingBottom: 8 }}>
          <Text style={{ fontSize: 16, fontWeight: '800', color: ui.textPrimary }}>Move “{entryTitle(entry)}”</Text>
          <Text style={{ fontSize: 12, color: ui.textSecondary, marginTop: 4 }}>
            Pick where it goes.{entry.seriesId ? ' This moves every day of the item.' : ''}
          </Text>
        </View>
        <ScrollView contentContainerStyle={{ paddingHorizontal: 18, paddingBottom: 12, gap: 14 }}>
          {cats.map((cat) => (
            <View key={cat.id} style={{ gap: 8 }}>
              <Text style={{ fontSize: 13, fontWeight: '700', color: ui.textPrimary }}>{cat.label}</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                <Pill ui={ui} small label="No subheader" on={entry.category === cat.id && !entry.subheader} onPress={() => onPick(cat.id, '')} />
                {subheadersFor(subheaders, cat.id).map((s) => (
                  <Pill key={s.id} ui={ui} small label={s.name} on={entry.subheader === s.id} onPress={() => onPick(cat.id, s.id)} />
                ))}
                <InlinePillAdd ui={ui} label="New subheader" placeholder="Subheader name" onAdd={(n) => onCreate(cat.id, n)} />
              </View>
            </View>
          ))}
        </ScrollView>
        <TouchableOpacity onPress={onCancel} style={{ padding: 14, alignItems: 'center', borderTopWidth: 1, borderTopColor: ui.border }}>
          <Text style={{ fontSize: 14, fontWeight: '600', color: ui.textSecondary }}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

// One line item in the list: title, how it is counted, amount, and its actions. Per-day items fold into one row.
function EntryRow({ ui, row, showCategory, expanded, onToggle, onEdit, onMove, onDelete, onEditOccurrence, onDeleteOccurrence }) {
  const e = row.first;
  const merch = isMerchEntry(e);
  const isSeries = row.kind === 'series';
  const flows = row.items.reduce((acc, it) => {
    const f = entryFlows(it);
    acc.income += f.income; acc.expense += f.expense;
    return acc;
  }, { income: 0, expense: 0 });
  const showIncome = flows.income > 0;
  const amountText = showIncome
    ? `+${usd(flows.income)}`
    : (flows.expense > 0 ? `−${usd(flows.expense)}` : usd(0));
  const amountColor = showIncome ? '#2ecc71' : (flows.expense > 0 ? '#e74c3c' : ui.textSecondary);
  const last = row.items[row.items.length - 1];
  const bits = [];
  if (showCategory) bits.push(categoryLabel(e.category));
  if (merch) {
    const m = calcMerch(e);
    if (e.band) bits.push(e.band);
    bits.push(`Hard ${usd(m.hardGross)} · Soft ${usd(m.softGross)}`);
    if (m.production > 0) bits.push(`Production ${usd(m.production)}`);
    bits.push(formatDay(e.date, false));
  } else if (isSeries) {
    bits.push(seriesRateLabel(row.items));
    bits.push(`${formatDay(e.date, false)} to ${formatDay(last.date, false)}`);
  } else {
    bits.push(unitLabel(e));
    bits.push(formatDay(e.date, false));
  }
  const note = entryNote(e);
  const expandable = !!note || isSeries;
  const iconBtn = { width: 34, height: 34, alignItems: 'center', justifyContent: 'center' };
  return (
    <View style={{ paddingVertical: 10, borderTopWidth: 1, borderTopColor: ui.border }}>
      {/* Tapping the item opens it for editing; the chevron shows its note or days. */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
        <TouchableOpacity onPress={onEdit} accessibilityLabel={`Edit ${entryTitle(e)}`} style={{ flex: 1, flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
          <View style={{ flex: 1, gap: 3 }}>
            <Text style={{ fontSize: 14, fontWeight: '700', color: ui.textPrimary }} numberOfLines={1}>{entryTitle(e)}</Text>
            <Text style={{ fontSize: 12, color: ui.textSecondary }} numberOfLines={2}>{bits.join(' · ')}</Text>
          </View>
          <Text style={{ fontSize: 14, fontWeight: '700', color: amountColor, marginTop: 1 }}>{amountText}</Text>
        </TouchableOpacity>
        {expandable ? (
          <TouchableOpacity onPress={onToggle} accessibilityLabel={expanded ? 'Hide details' : 'Show details'} style={{ padding: 2 }}>
            <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={15} color={ui.textSecondary} />
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={{ flexDirection: 'row', alignSelf: 'flex-end', marginTop: 2 }}>
        <TouchableOpacity onPress={onEdit} accessibilityLabel="Edit entry" style={iconBtn}>
          <Ionicons name="create-outline" size={16} color={ui.textSecondary} />
        </TouchableOpacity>
        <TouchableOpacity onPress={onMove} accessibilityLabel="Move entry" style={iconBtn}>
          <Ionicons name="folder-open-outline" size={16} color={ui.textSecondary} />
        </TouchableOpacity>
        <TouchableOpacity onPress={onDelete} accessibilityLabel="Delete entry" style={iconBtn}>
          <Ionicons name="trash-outline" size={16} color="rgba(231,76,60,0.75)" />
        </TouchableOpacity>
      </View>
      {expanded ? (
        <View style={{ marginTop: 6, gap: 8 }}>
          {note ? <Text style={{ fontSize: 13, color: ui.textPrimary, lineHeight: 18 }}>{note}</Text> : null}
          {isSeries ? (
            <View style={{ backgroundColor: ui.surface, borderRadius: 10, paddingHorizontal: 10 }}>
              {row.items.map((it, idx) => (
                <View key={it.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderTopWidth: idx ? 1 : 0, borderTopColor: ui.border }}>
                  <Text style={{ flex: 1, fontSize: 12, color: ui.textSecondary }}>{formatDay(it.date)}</Text>
                  <Text style={{ fontSize: 12, fontWeight: '600', color: ui.textPrimary }}>{usd(lineTotal(it))}</Text>
                  <TouchableOpacity onPress={() => onEditOccurrence(it)} accessibilityLabel="Edit this day" style={{ padding: 6 }}>
                    <Ionicons name="create-outline" size={14} color={ui.textSecondary} />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => onDeleteOccurrence(it)} accessibilityLabel="Delete this day" style={{ padding: 6 }}>
                    <Ionicons name="trash-outline" size={14} color="rgba(231,76,60,0.75)" />
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ExpenseTrackerModal({ visible, onClose }) {
  const { theme, isDarkMode } = useTheme();
  const { user } = useUser();
  // Budget revisions from the 2026-10-21 Lauren meeting (Admin switch). Off keeps the old layout.
  const revised = useBudgetRevisions();
  const [tab, setTab] = useState('overview'); // 'overview' | 'add'
  const [view, setView] = useState('category'); // 'category' | 'day'
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterTour, setFilterTour] = useState('all');
  const [userFolders, setUserFolders] = useState([]);
  const [subheaders, setSubheaders] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [dialog, setDialog] = useState(null); // { kind, entry, ... }
  const [moving, setMoving] = useState(null);
  const [renaming, setRenaming] = useState(null); // { id, text }
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const settingsReady = useRef(false);

  // Revised: no "All Tours". Open on a real tour (the one running today, or the next one).
  useEffect(() => {
    if (!revised) {
      if (filterTour !== 'all') setFilterTour('all');
      return;
    }
    if (!userFolders.length) return;
    const ids = userFolders.map((f) => f.id);
    const orphanOrNone = entries.some((e) => !e.linkedTourId || !ids.includes(e.linkedTourId));
    const valid = (filterTour === 'none' && orphanOrNone) || ids.includes(filterTour);
    if (!valid) setFilterTour(pickDefaultTour(userFolders));
  }, [revised, userFolders, filterTour, entries]);

  // The switch can arrive after the rail mounts; start a blank form that matches it.
  useEffect(() => {
    if (!editingId) resetForm();
  }, [revised]);
  const tourForNewEntries = () => (
    revised && filterTour && filterTour !== 'all' && filterTour !== 'none' ? filterTour : ''
  );
  // Revised: picking a tour moves the dates onto it (today if the tour is on now, else its first day).
  const applyTourDates = (tourId) => {
    const { date, start, end } = tourEntryDates((userFolders.find((f) => f.id === tourId) || {}).dayKeys, todayKey());
    setEntryDate(date);
    setRangeStart(start);
    setRangeEnd(end);
  };
  // The calendar's Show / Travel / Off colors, which the user can change there.
  const [laneColors, setLaneColors] = useState(() => loadV2Prefs().laneColors);
  // Revised: the user's own categories (Renee's Oct 17 feature, same Firestore fields).
  const [customCategories, setCustomCategories] = useState({ expense: [], income: [] });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.addEventListener) return undefined;
    const sync = () => setLaneColors(loadV2Prefs().laneColors);
    window.addEventListener(V2_PREFS_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(V2_PREFS_EVENT, sync); window.removeEventListener('storage', sync); };
  }, []);
  useEffect(() => { if (visible) setLaneColors(loadV2Prefs().laneColors); }, [visible]);

  // Add-entry form state
  const [entryType, setEntryType] = useState('expense'); // 'expense' | 'income'
  const [category, setCategory] = useState(revised ? '' : 'crew');
  const [subheaderId, setSubheaderId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Non-merch entries are a stack of line items; each has its own name, unit choice, amount, and note.
  const [lines, setLines] = useState(() => [newLine(revised ? '' : 'one_off')]);
  const [entryDate, setEntryDate] = useState(todayKey());
  const [rangeStart, setRangeStart] = useState(todayKey());
  const [rangeEnd, setRangeEnd] = useState(todayKey());
  const [linkedTourId, setLinkedTourId] = useState('');
  const [tourTouched, setTourTouched] = useState(false); // revised: user picked this entry's tour by hand
  // Merch fields
  const [hardGross, setHardGross] = useState('');
  const [softGross, setSoftGross] = useState('');
  const [hardBandPct, setHardBandPct] = useState(String(MERCH_DEFAULT_HARD_BAND_PCT));
  const [softBandPct, setSoftBandPct] = useState(String(MERCH_DEFAULT_SOFT_BAND_PCT));
  const [sellerFee, setSellerFee] = useState('');
  const [sellerFeeType, setSellerFeeType] = useState('flat'); // 'flat' | 'pct' | 'hourly'
  const [sellerFeeNote, setSellerFeeNote] = useState('');
  const [sellerFeeHours, setSellerFeeHours] = useState('');
  const [productionLines, setProductionLines] = useState([]);
  const [band, setBand] = useState('');
  const [hardCustomOpen, setHardCustomOpen] = useState(false);
  const [softCustomOpen, setSoftCustomOpen] = useState(false);
  const [editingId, setEditingId] = useState('');

  const isMerchForm = entryType === 'expense' && category === 'merch';

  // Load entries, tours, and subheaders on open
  useEffect(() => {
    if (visible && user?.uid) {
      loadEntries();
      loadFolders();
      loadSettings();
    }
  }, [visible, user?.uid]);

  const loadFolders = async () => {
    if (!user?.uid) return;
    try {
      const db = getFirestore();
      const snap = await getDoc(doc(db, 'users', user.uid));
      const profileDoc = (snap.exists() && snap.data()) || {};
      setCustomCategories({
        expense: cleanCustomCategories(profileDoc[CUSTOM_CATEGORY_FIELDS.expense]),
        income: cleanCustomCategories(profileDoc[CUSTOM_CATEGORY_FIELDS.income]),
      });
      const raw = profileDoc.folders || {};
      setUserFolders(
        Object.entries(raw)
          .filter(([, f]) => f?.tour)
          .map(([id, f]) => ({ id, name: f.tour?.name || f.name || 'Untitled', dayKeys: tourDayKeys(f.tour), kinds: revised && tourDayKeys(f.tour).length <= 400 ? tourDayKinds(f.tour) : {} }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (_) {}
  };

  const loadSettings = async () => {
    if (!user?.uid) return;
    try {
      const snap = await getDoc(doc(getFirestore(), 'users', user.uid, 'settings', 'expenseTracker'));
      const list = snap.exists() ? snap.data().subheaders : null;
      setSubheaders(Array.isArray(list) ? list.filter((s) => s && s.id && s.category && s.name) : []);
      settingsReady.current = true;
    } catch (_) {}
  };

  // A save replaces the whole list, so never save before the stored list has been read.
  const subheadersReady = () => {
    if (settingsReady.current) return true;
    setNotice('Your subheaders are still loading. Try again in a moment.');
    loadSettings();
    return false;
  };

  const saveSubheaders = async (next) => {
    setSubheaders(next);
    if (!user?.uid) return;
    try {
      await setDoc(doc(getFirestore(), 'users', user.uid, 'settings', 'expenseTracker'), { subheaders: next }, { merge: true });
    } catch (e) {
      console.warn('ExpenseTracker subheader save error:', e.message);
    }
  };

  const loadEntries = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const db = getFirestore();
      const snap = await getDocs(
        query(collection(db, 'users', user.uid, 'expenses'), orderBy('createdAt', 'desc'))
      );
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.warn('ExpenseTracker load error:', e.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEntryType('expense'); setCategory(revised ? '' : 'crew'); setSubheaderId(''); setName(''); setDescription(''); setLines([newLine(revised ? '' : 'one_off')]);
    setEntryDate(todayKey()); setRangeStart(todayKey()); setRangeEnd(todayKey()); setLinkedTourId(revised ? tourForNewEntries() : ''); setTourTouched(false);
    if (revised) applyTourDates(tourForNewEntries());
    setHardGross(''); setSoftGross('');
    setHardBandPct(String(MERCH_DEFAULT_HARD_BAND_PCT)); setSoftBandPct(String(MERCH_DEFAULT_SOFT_BAND_PCT));
    setSellerFee(''); setSellerFeeType('flat'); setSellerFeeNote(''); setSellerFeeHours(''); setProductionLines([]);
    setBand(''); setHardCustomOpen(false); setSoftCustomOpen(false); setEditingId(''); setFormError('');
  };

  // Revised: a user adds their own category; it is saved on their profile and picked straight away.
  // select: false when added from the Overview (don't touch the Add form).
  const addCustomCategory = async (type, label, { select = true } = {}) => {
    const name = String(label || '').trim();
    const base = customCategoryId(label);
    if (!name || !base) { setNotice('Use letters or numbers in the category name.'); return; }
    const same = [...(type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES), ...(customCategories[type] || [])];
    const existing = same.find((c) => c.id === base || c.label.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!select) { setNotice(`“${existing.label}” is already a category.`); return; }
      if (existing.id === 'merch' || existing.id === 'merch_net') { setEntryType('expense'); setCategory('merch'); } else setCategory(existing.id);
      setSubheaderId('');
      return;
    }
    // Never reuse a built-in id from the other side (e.g. "Merch" typed under Income).
    const builtIn = [...EXPENSE_CATEGORIES, ...INCOME_CATEGORIES].some((c) => c.id === base);
    const id = builtIn ? `${type}-${base}` : base;
    const item = { id, label: name };
    setCustomCategories((prev) => ({ ...prev, [type]: cleanCustomCategories([...(prev[type] || []), item]) }));
    if (select) { setCategory(id); setSubheaderId(''); }
    if (!user?.uid) return;
    try {
      await setDoc(doc(getFirestore(), 'users', user.uid), { [CUSTOM_CATEGORY_FIELDS[type]]: arrayUnion(item) }, { merge: true });
    } catch (e) {
      setNotice(`Could not save the new category: ${e.message}`);
    }
  };

  // Revised: delete one of the user's own categories. Empty goes straight away; otherwise type its name.
  const [deletingCategory, setDeletingCategory] = useState(false);
  // Read the category's entries straight from the database (every tour, every device), never the
  // on-screen list, which can be stale or empty if it failed to load.
  const fetchCategoryEntries = async (id) => {
    const snap = await getDocs(query(collection(getFirestore(), 'users', user.uid, 'expenses'), where('category', '==', id)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };
  const requestDeleteCategory = async (type, cat) => {
    if (!user?.uid || loading || deletingCategory) return;
    if (!subheadersReady()) return;
    try {
      const plan = categoryDeletePlan(await fetchCategoryEntries(cat.id), subheaders, type, cat.id);
      if (!plan.needsTypedConfirm) { performDeleteCategory(type, cat.id); return; }
      setDialog({ kind: 'delcat', type, id: cat.id, name: cat.label, count: plan.entries.length });
    } catch (e) {
      setNotice(`Could not check that category: ${e.message}`);
    }
  };
  const performDeleteCategory = async (type, id) => {
    if (!user?.uid || deletingCategory) return;
    if (!subheadersReady()) return;
    setDeletingCategory(true);
    try {
      const plan = categoryDeletePlan(await fetchCategoryEntries(id), subheaders, type, id);
      if (plan.entries.length) {
        await writeInChunks(plan.entries, (batch, ref) => batch.delete(ref));
        const gone = new Set(plan.entries.map((e) => e.id));
        setEntries((prev) => prev.filter((e) => !gone.has(e.id)));
        if (editingId && gone.has(editingId)) resetForm();
      }
      if (plan.subheaders.length) {
        await saveSubheaders(subheaders.filter((s) => s.category !== id));
      }
      // Rewrite the list without it (arrayRemove needs an exact object match, and older rows carry extra fields).
      const db = getFirestore();
      const ref = doc(db, 'users', user.uid);
      const field = CUSTOM_CATEGORY_FIELDS[type];
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        const stored = (snap.exists() && Array.isArray(snap.data()[field])) ? snap.data()[field] : [];
        tx.set(ref, { [field]: stored.filter((c) => !c || c.id !== id) }, { merge: true });
      });
      setCustomCategories((prev) => ({ ...prev, [type]: (prev[type] || []).filter((c) => c.id !== id) }));
      if (category === id) { setCategory(''); setSubheaderId(''); }
      setDialog(null);
    } catch (e) {
      setNotice(`Could not delete the category: ${e.message}`);
    } finally {
      setDeletingCategory(false);
    }
  };

  // Revised: "+ Add expense" inside a category or subheader opens a blank form already set to it.
  const startAddFor = (type, cat, sub) => {
    resetForm();
    const merch = cat === 'merch' || cat === 'merch_net';
    setEntryType(merch ? 'expense' : type);
    setCategory(merch ? 'merch' : (cat || ''));
    // Merch subheaders live under Expense › Merch; Income › Merch Net has none to carry over.
    setSubheaderId(cat === 'merch_net' ? '' : (sub || ''));
    setTab('add');
  };

  // Load a saved entry back into the form so its split, amounts, and notes can be changed.
  const startEdit = (entry) => {
    resetForm();
    setEditingId(entry.id);
    setEntryType(entry.type === 'income' ? 'income' : 'expense');
    setCategory(entry.category || 'other');
    setSubheaderId(entry.subheader || '');
    setEntryDate(toDayKey(entry.date) || todayKey());
    setLinkedTourId(entry.linkedTourId || '');
    if (isMerchEntry(entry)) {
      const m = calcMerch(entry);
      setName(entry.name || '');
      setDescription(entry.description || '');
      setHardGross(m.hardGross ? String(m.hardGross) : '');
      setSoftGross(m.softGross ? String(m.softGross) : '');
      setHardBandPct(String(m.hardBandPct));
      setSoftBandPct(String(m.softBandPct));
      setHardCustomOpen(!merchPresetFor(m.hardBandPct));
      setSoftCustomOpen(!merchPresetFor(m.softBandPct));
      setSellerFee(entry.sellerFee ? String(entry.sellerFee) : '');
      setSellerFeeType(entry.sellerFeeType || 'flat');
      setSellerFeeNote(entry.sellerFeeNote || '');
      setSellerFeeHours(entry.sellerFeeHours ? String(entry.sellerFeeHours) : '');
      setBand(entry.band || '');
      const lines = Array.isArray(entry.productionItems) && entry.productionItems.length
        ? entry.productionItems.map((l) => ({
          id: l.id || lineId(), name: l.name || '', unitMode: l.unitMode === 'units' ? 'units' : 'one_off',
          amount: l.amount ? String(l.amount) : '', unitCount: l.unitCount ? String(l.unitCount) : '',
          unitPrice: l.unitPrice ? String(l.unitPrice) : '', note: l.note || '',
        }))
        : (m.production > 0 ? [{ ...emptyLine(), name: 'Production cost', amount: String(m.production) }] : []);
      setProductionLines(lines);
    } else {
      // Older entries used the description as their only title.
      const mode = entry.unitMode || 'one_off';
      setLines([{
        id: lineId(),
        name: entry.name || entry.description || '',
        unitMode: mode,
        amount: mode !== 'units' && entry.amount ? String(entry.amount) : '',
        unitCount: mode === 'units' && entry.unitCount ? String(entry.unitCount) : '',
        unitPrice: mode === 'units' && entry.unitPrice ? String(entry.unitPrice) : '',
        description: entry.name ? (entry.description || '') : '',
        descOpen: !!(entry.name && entry.description),
      }]);
    }
    setTab('add');
  };

  const cancelEdit = () => {
    resetForm();
    setTab('overview');
  };

  const patchLine = (id, next) => setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...next } : l)));
  const addLine = () => setLines((prev) => [...prev, newLine(revised ? '' : 'one_off')]);
  const removeLine = (id) => setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.id !== id) : prev));

  // Leaving the Add tab or closing the rail ends an edit, so "+ Add Entry" is always a blank form.
  const goTab = (id) => {
    if (id !== 'add' && editingId) resetForm();
    // Revised: a new entry starts on the tour picked in Overview.
    // Only move the dates when the tour actually changes, so a date the user picked survives a trip to Overview.
    if (id === 'add' && !editingId && revised && !tourTouched && tourForNewEntries() !== linkedTourId) { setLinkedTourId(tourForNewEntries()); applyTourDates(tourForNewEntries()); }
    setTab(id);
  };
  const closeRail = () => {
    if (editingId) resetForm();
    setDialog(null);
    setMoving(null);
    onClose();
  };

  const selectedTour = userFolders.find((f) => f.id === linkedTourId) || null;
  const tourDays = selectedTour ? selectedTour.dayKeys : [];
  const editingEntry = editingId ? entries.find((e) => e.id === editingId) : null;
  const editingSeries = !!(editingEntry && editingEntry.seriesId);

  const merchFields = () => {
    const lines = productionLines
      .map((l) => ({
        id: l.id,
        name: String(l.name || '').trim(),
        unitMode: l.unitMode === 'units' ? 'units' : 'one_off',
        amount: toNum(l.amount),
        unitCount: toNum(l.unitCount),
        unitPrice: toNum(l.unitPrice),
        note: String(l.note || '').trim(),
      }))
      .filter((l) => l.name || (l.unitMode === 'units' ? l.unitCount * l.unitPrice : l.amount) > 0);
    const m = calcMerch({ hardGross, softGross, hardBandPct, softBandPct, sellerFee, sellerFeeType, sellerFeeHours, productionItems: lines });
    return {
      hardGross: m.hardGross,
      softGross: m.softGross,
      hardBandPct: m.hardBandPct,
      softBandPct: m.softBandPct,
      sellerFee: toNum(sellerFee),
      sellerFeeType,
      sellerFeeNote: sellerFeeNote.trim() || null,
      sellerFeeHours: sellerFeeType === 'hourly' ? toNum(sellerFeeHours) : null,
      productionItems: lines,
      productionCost: m.production,
      band: band.trim() || null,
      amount: m.gross,
      merchNet: parseFloat(m.net.toFixed(2)),
    };
  };

  const moneyFieldsFor = (l) => (l.unitMode === 'units'
    ? { unitMode: 'units', unitCount: toNum(l.unitCount), unitPrice: toNum(l.unitPrice), amount: toNum(l.unitCount) * toNum(l.unitPrice) }
    : { unitMode: l.unitMode, amount: toNum(l.amount) });

  const activeLines = () => lines.filter(lineHasContent);

  const validate = () => {
    if (revised && !category) return 'Pick a category.';
    if (isMerchForm) {
      if (sellerFeeType === 'hourly' && toNum(sellerFee) > 0 && toNum(sellerFeeHours) <= 0) return 'Enter the hours for the hourly seller fee.';
      return '';
    }
    const active = activeLines();
    if (!active.length) return 'Add a line item with a name and an amount.';
    for (const l of active) {
      const tag = lines.length > 1 ? `Line ${lines.indexOf(l) + 1}: ` : '';
      if (!l.name.trim()) return `${tag}Give this item a name.`;
      if (revised && !l.unitMode) return `${tag}Choose how it is counted: one-off, per day, or # of units.`;
      if (l.unitMode === 'units') {
        if (toNum(l.unitCount) <= 0 || toNum(l.unitPrice) <= 0) return `${tag}Enter the number of units and the price of each.`;
      } else if (toNum(l.amount) <= 0) {
        return `${tag}Enter an amount.`;
      }
      if (l.unitMode === 'per_day' && !editingId
        && !plannedDays({ unitMode: 'per_day', dateKey: entryDate, rangeStart, rangeEnd, tourDays }).length) {
        return `${tag}Pick a start and end date, or link a tour.`;
      }
    }
    return '';
  };

  const handleSave = async () => {
    if (!user?.uid || saving) return;
    const problem = validate();
    setFormError(problem);
    if (problem) return;
    if (editingEntry && editingEntry.seriesId) {
      setDialog({ kind: 'edit', entry: editingEntry });
      return;
    }
    await commitSave('this');
  };

  // What a save writes. The Save bar's net is worked out from these same fields.
  const baseFields = () => ({
    type: entryType,
    category,
    linkedTourId: linkedTourId || null,
    subheader: subheaderId || null,
  });

  const editFields = () => (isMerchForm
    ? { ...baseFields(), name: name.trim() || null, description: description.trim() || null, ...merchFields() }
    : { ...baseFields(), name: lines[0].name.trim() || null, description: lines[0].description.trim() || null, ...moneyFieldsFor(lines[0]) });

  // New entries: one document per line item per day. A per-day line shares a series id.
  const newDocs = () => {
    const base = baseFields();
    const docs = [];
    if (isMerchForm) {
      docs.push({ ...base, name: name.trim() || null, description: description.trim() || null, ...merchFields(), date: toDayKey(entryDate) || todayKey() });
      return docs;
    }
    activeLines().forEach((l) => {
      const days = plannedDays({ unitMode: l.unitMode, dateKey: entryDate, rangeStart, rangeEnd, tourDays });
      const seriesId = l.unitMode === 'per_day' ? newSeriesId() : null;
      days.forEach((day) => docs.push({
        ...base, name: l.name.trim() || null, description: l.description.trim() || null,
        ...moneyFieldsFor(l), date: day, ...(seriesId ? { seriesId } : {}),
      }));
    });
    return docs;
  };

  const commitSave = async (scope) => {
    setSaving(true);
    try {
      const db = getFirestore();
      if (editingEntry) {
        const fields = editFields();
        const targets = scopeTargets(entries, editingEntry, scope);
        await writeInChunks(targets, (batch, ref, t) => {
          const patch = { ...fields };
          if (scope === 'this' || !editingEntry.seriesId) patch.date = toDayKey(entryDate) || t.date;
          if (!isMerchForm && isMerchEntry(t)) MERCH_ENTRY_FIELDS.forEach((key) => { patch[key] = deleteField(); });
          batch.update(ref, patch);
        });
      } else {
        const docs = newDocs();
        for (const part of chunkArray(docs)) {
          const batch = writeBatch(db);
          part.forEach((d) => batch.set(doc(collection(db, 'users', user.uid, 'expenses')), { ...d, createdAt: serverTimestamp() }));
          await batch.commit();
        }
      }
      resetForm();
      setTab('overview');
      loadEntries();
    } catch (e) {
      setFormError(`Could not save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete, move, subheaders ───────────────────────────────────────────────

  // Writes go in groups well under Firestore's 500-per-batch limit.
  const writeInChunks = async (targets, apply) => {
    const db = getFirestore();
    for (const part of chunkArray(targets)) {
      const batch = writeBatch(db);
      part.forEach((t) => apply(batch, doc(db, 'users', user.uid, 'expenses', t.id), t));
      await batch.commit();
    }
  };

  // rowItems: when a folded per-day row is deleted, remove exactly the days that row shows.
  const performDelete = async (entry, scope, rowItems) => {
    if (!user?.uid) return;
    const targets = rowItems && rowItems.length ? rowItems : scopeTargets(entries, entry, scope);
    try {
      await writeInChunks(targets, (batch, ref) => batch.delete(ref));
      const gone = new Set(targets.map((t) => t.id));
      if (editingId && gone.has(editingId)) resetForm();
      setEntries((prev) => prev.filter((e) => !gone.has(e.id)));
    } catch (e) {
      setNotice(`Could not delete: ${e.message}`);
    }
  };

  const requestDelete = (entry, wholeSeries, rowItems) => {
    if (!entry.seriesId) { performDelete(entry, 'this'); return; }
    setDialog({ kind: 'delete', entry, wholeSeries: !!wholeSeries, rowItems });
  };

  const applyMove = async (entry, nextCategory, nextSubheader) => {
    if (!user?.uid) return;
    const targets = entry.seriesId ? scopeTargets(entries, entry, 'all') : [entry];
    const patch = movePatch(nextCategory, nextSubheader, entry.type);
    try {
      await writeInChunks(targets, (batch, ref) => batch.update(ref, patch));
      const ids = new Set(targets.map((t) => t.id));
      setEntries((prev) => prev.map((e) => (ids.has(e.id) ? { ...e, ...patch } : e)));
      setMoving(null);
    } catch (e) {
      setMoving(null);
      setNotice(`Could not move: ${e.message}`);
    }
  };

  const createSubheader = (cat, text) => {
    if (!subheadersReady()) return '';
    const { list, id } = addSubheader(subheaders, cat, text);
    if (id && list !== subheaders) saveSubheaders(list);
    return id;
  };

  const performDeleteSubheader = async (id) => {
    if (!subheadersReady()) return;
    const affected = entries.filter((e) => e.subheader === id);
    saveSubheaders(removeSubheader(subheaders, id));
    if (!affected.length || !user?.uid) return;
    try {
      await writeInChunks(affected, (batch, ref) => batch.update(ref, { subheader: null }));
      const ids = new Set(affected.map((t) => t.id));
      setEntries((prev) => prev.map((e) => (ids.has(e.id) ? { ...e, subheader: null } : e)));
    } catch (e) {
      // The list no longer has this subheader, so these entries show as unsorted either way.
      setNotice(`Subheader removed, but some items could not be cleaned up: ${e.message}`);
    }
  };

  // ─── Filtered entries & totals ───────────────────────────────────────────────

  const knownTourIds = userFolders.map((f) => f.id);
  // The tour being viewed in Overview, for the By day colors (none for "No tour").
  const viewedKindsRaw = (userFolders.find((f) => f.id === filterTour) || {}).kinds;
  const viewedTourKinds = viewedKindsRaw && Object.keys(viewedKindsRaw).length ? viewedKindsRaw : null;
  const filtered = entriesForTour(entries, filterTour, knownTourIds);
  // "No tour" also holds entries whose tour was deleted, so nothing is hidden.
  const hasUnlinked = entries.some((e) => !e.linkedTourId || !knownTourIds.includes(e.linkedTourId));
  const tourName = (id) => (id && id !== 'none' && id !== 'all'
    ? ((userFolders.find((f) => f.id === id) || {}).name || 'this tour')
    : 'no tour');

  const totals = overviewTotals(filtered);
  const net = totals.net;
  const merchTotals = merchSummary(filtered);
  const merchBands = merchByBand(filtered);
  const bandNames = [...new Set(entries.filter(isMerchEntry).map(e => String(e.band || '').trim()).filter(Boolean))];
  const liveMerch = calcMerch({
    hardGross, softGross, hardBandPct, softBandPct, sellerFee, sellerFeeType, sellerFeeHours,
    productionItems: productionLines.map((l) => ({ unitMode: l.unitMode, amount: l.amount, unitCount: l.unitCount, unitPrice: l.unitPrice })),
  });
  const perDayLines = lines.filter((l) => l.unitMode === 'per_day');
  const showPeriod = !isMerchForm && !editingId && perDayLines.length > 0;
  const showDate = isMerchForm || !!editingId || lines.some((l) => l.unitMode !== 'per_day');
  const periodDays = showPeriod
    ? plannedDays({ unitMode: 'per_day', dateKey: entryDate, rangeStart, rangeEnd, tourDays })
    : [];
  const perDayTotal = perDayLines.reduce((sum, l) => sum + toNum(l.amount), 0) * periodDays.length;
  // The Save bar's net: what this form adds, and the net across every entry once it is saved.
  // A per-day item edit can reach every day, so the bar shows it applied to all of them.
  const draftTargets = editingEntry ? (editingSeries ? scopeTargets(entries, editingEntry, 'all') : [editingEntry]) : [];
  const draft = tab === 'add'
    ? (editingEntry ? draftTargets.map((t) => ({ ...t, ...editFields(), date: t.date })) : newDocs())
    : [];
  // Revised: the Save bar's net covers the tour this entry goes to, not every tour.
  const saveTotals = draftTotals(revised ? entriesForTour(entries, linkedTourId || 'none', knownTourIds) : entries, draft, draftTargets.map((t) => t.id));
  const expenseGroups = useMemo(() => groupEntries(filtered, 'expense', subheaders, customCategories.expense, revised ? customCategories.expense.map((c) => c.id) : []), [filtered, subheaders, customCategories, revised]);
  const incomeGroups = useMemo(() => groupEntries(filtered, 'income', subheaders, customCategories.income, revised ? customCategories.income.map((c) => c.id) : []), [filtered, subheaders, customCategories, revised]);
  const dayGroups = useMemo(() => groupByDay(filtered), [filtered]);

  // ─── Styles ───────────────────────────────────────────────────────────────────

  const bg = isDarkMode ? '#12141f' : '#ffffff';
  const surface = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const card = isDarkMode ? 'rgba(255,255,255,0.035)' : '#fbfbfd';
  const border = isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.09)';
  const textPrimary = isDarkMode ? 'rgba(255,255,255,0.92)' : '#111';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(17,17,17,0.5)';
  const inputBg = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const inputText = isDarkMode ? '#fff' : '#111';
  const placeholder = isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';

  const ui = {
    dark: isDarkMode, bg, surface, card, border, textPrimary, textSecondary, placeholder,
    label: { fontSize: 11, fontWeight: '700', color: textSecondary, textTransform: 'uppercase', letterSpacing: 0.7 },
    input: {
      backgroundColor: inputBg, borderRadius: 10, borderWidth: 1, borderColor: border,
      paddingHorizontal: 13, paddingVertical: 11, fontSize: 14, color: inputText, width: '100%',
    },
  };

  // Revised: Category / Subheader / Tour labels read as headings, not faint captions.
  const strongLabel = { color: textPrimary, fontWeight: '800', fontSize: 12, letterSpacing: 0.6 };

  const sectionTitle = (text, right) => (
    <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 4 }}>
      <Text style={{ fontSize: 13, fontWeight: '800', color: textPrimary, letterSpacing: 0.4, textTransform: 'uppercase' }}>{text}</Text>
      {right}
    </View>
  );

  const addHere = (type, cat, sub, where) => (
    <TouchableOpacity
      onPress={() => startAddFor(type, cat, sub)}
      accessibilityLabel={`Add ${type === 'income' ? 'income' : 'an expense'}${where ? ` to ${where}` : ''}`}
      style={{ alignSelf: 'flex-start', paddingVertical: 8, paddingHorizontal: 4 }}
    >
      <Text style={{ fontSize: 13, fontWeight: '700', color: '#1e9e57' }}>
        + Add {cat === 'merch' || cat === 'merch_net' ? 'merch' : (type === 'income' ? 'income' : 'expense')}
      </Text>
    </TouchableOpacity>
  );

  const renderGroups = (groups, emptyText, type) => (
    groups.length === 0 ? (
      <View style={{ gap: 2 }}>
        <Text style={{ fontSize: 13, color: textSecondary }}>{emptyText}</Text>
        {revised ? addHere(type, '', '', '') : null}
      </View>
    ) : groups.map((cat) => (
      <Card key={cat.id} ui={ui} style={{ gap: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 6 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
            <Ionicons name={cat.icon || 'pricetag-outline'} size={16} color={textSecondary} />
            <Text style={{ fontSize: 15, fontWeight: '800', color: textPrimary }}>{cat.label}</Text>
            {revised && (customCategories[type] || []).some((c) => c.id === cat.id) ? (
              <TouchableOpacity onPress={() => requestDeleteCategory(type, cat)} accessibilityLabel={`Delete the ${cat.label} category`} style={{ padding: 4 }}>
                <Ionicons name="close-circle-outline" size={16} color="rgba(231,76,60,0.8)" />
              </TouchableOpacity>
            ) : null}
          </View>
          <Text style={{ fontSize: 14, fontWeight: '800' }}>
            {cat.income > 0 ? <Text style={{ color: '#2ecc71' }}>+{usd(cat.income)} </Text> : null}
            {cat.expense > 0 || cat.income === 0 ? <Text style={{ color: cat.expense > 0 ? '#e74c3c' : textSecondary }}>{cat.expense > 0 ? '−' : ''}{usd(cat.expense)}</Text> : null}
          </Text>
        </View>
        {cat.groups.map((g) => (
          <View key={g.id || 'unsorted'} style={{ marginTop: g.name ? 6 : 0 }}>
            {g.name ? (
              <View style={revised
                ? { flexDirection: 'row', alignItems: 'center', paddingLeft: 2, marginTop: 6, borderBottomWidth: 1, borderBottomColor: border }
                : { flexDirection: 'row', alignItems: 'center', backgroundColor: surface, borderRadius: 10, paddingLeft: 10, paddingVertical: 4, marginTop: 4 }}>
                {renaming && renaming.id === g.id ? (
                  <TextInput
                    autoFocus
                    style={{ flex: 1, fontSize: 13, fontWeight: '700', color: textPrimary, paddingVertical: 6 }}
                    value={renaming.text}
                    onChangeText={(t) => setRenaming({ id: g.id, text: t })}
                    onSubmitEditing={() => { if (subheadersReady()) saveSubheaders(renameSubheader(subheaders, g.id, renaming.text)); setRenaming(null); }}
                    onBlur={() => { if (renaming) { if (subheadersReady()) saveSubheaders(renameSubheader(subheaders, g.id, renaming.text)); setRenaming(null); } }}
                  />
                ) : (
                  <Text style={revised
                    ? { flex: 1, fontSize: 11, fontWeight: '800', color: textPrimary, textTransform: 'uppercase', letterSpacing: 0.7 }
                    : { flex: 1, fontSize: 13, fontWeight: '700', color: textPrimary }} numberOfLines={1}>{g.name}</Text>
                )}
                <Text style={{ fontSize: 12, fontWeight: '600', color: textSecondary, marginRight: 4 }}>{usd(g.income + g.expense)}</Text>
                <TouchableOpacity onPress={() => setRenaming({ id: g.id, text: g.name })} accessibilityLabel={`Rename ${g.name}`} style={{ padding: 8 }}>
                  <Ionicons name="create-outline" size={14} color={textSecondary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setDialog({ kind: 'delsub', id: g.id, name: g.name, count: g.items.length })} accessibilityLabel={`Delete ${g.name}`} style={{ padding: 8 }}>
                  <Ionicons name="trash-outline" size={14} color="rgba(231,76,60,0.75)" />
                </TouchableOpacity>
              </View>
            ) : null}
            {!revised && g.rows.length === 0 && g.name ? (
              <Text style={{ fontSize: 12, color: textSecondary, paddingVertical: 8, paddingHorizontal: 4 }}>No items here yet. Use Move on an item to put it here.</Text>
            ) : null}
            {g.rows.map((row) => (
              <View key={row.key} style={g.name ? { paddingLeft: 10, borderLeftWidth: 2, borderLeftColor: border, marginLeft: 6 } : null}>
              <EntryRow
                ui={ui} row={row}
                expanded={!!expanded[row.key]}
                onToggle={() => setExpanded((p) => ({ ...p, [row.key]: !p[row.key] }))}
                onEdit={() => startEdit(row.first)}
                onMove={() => setMoving(row.first)}
                onDelete={() => requestDelete(row.first, row.kind === 'series', row.items)}
                onEditOccurrence={startEdit}
                onDeleteOccurrence={(it) => requestDelete(it, false)}
              />
              </View>
            ))}
            {/* Revised: add straight into this subheader */}
            {revised && g.name ? addHere(type, cat.id, g.id, g.name) : null}
          </View>
        ))}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 10 }}>
          {revised ? addHere(type, cat.id, '', cat.label) : null}
          <InlinePillAdd ui={ui} label="Subheader" placeholder={`New subheader in ${cat.label}`} onAdd={(n) => createSubheader(cat.id, n)} />
        </View>
      </Card>
    ))
  );

  // ─── Render ───────────────────────────────────────────────────────────────────

  // Hard / soft merch gross sales. Revised: shown last, tinted green, because it is income on an expense form.
  const merchGrossCard = (
    <Card ui={ui} style={revised ? { backgroundColor: 'rgba(46,204,113,0.12)', borderColor: 'rgba(46,204,113,0.45)' } : null}>
      {revised ? <Label ui={ui} style={[strongLabel, { color: '#1e9e57' }]}>Merch sales (income)</Label> : null}
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <View style={{ flex: 1, gap: 6 }}>
          <Label ui={ui}>Hard Merch (vinyl, CDs)</Label>
          <Field ui={ui} numeric value={hardGross} onChangeText={setHardGross} placeholder="Gross sales $" />
        </View>
        <View style={{ flex: 1, gap: 6 }}>
          <Label ui={ui}>Soft Merch (shirts, hats)</Label>
          <Field ui={ui} numeric value={softGross} onChangeText={setSoftGross} placeholder="Gross sales $" />
        </View>
      </View>
    </Card>
  );

  const tourFilterOptions = revised
    ? [...userFolders.map(f => ({ id: f.id, label: f.name || 'Untitled' })), ...(hasUnlinked ? [{ id: 'none', label: 'No tour' }] : [])]
    : [{ id: 'all', label: 'All Tours' }, ...userFolders.map(f => ({ id: f.id, label: f.name || 'Untitled' }))];
  const tourFilter = userFolders.length > 0 ? (
    <View style={{ gap: 8 }}>
      <Label ui={ui} style={revised ? strongLabel : null}>Tour</Label>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {tourFilterOptions.map(opt => (
          <Pill key={opt.id} ui={ui} small label={opt.label} on={filterTour === opt.id} onPress={() => setFilterTour(opt.id)} />
        ))}
      </View>
    </View>
  ) : null;

  const tourChips = (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
      <Pill ui={ui} small label={revised ? 'No tour' : 'None'} on={!linkedTourId} onPress={() => { setLinkedTourId(''); setTourTouched(true); if (revised) applyTourDates(''); }} />
      {userFolders.map(f => (
        <Pill key={f.id} ui={ui} small label={f.name || 'Untitled'} on={linkedTourId === f.id} onPress={() => { setLinkedTourId(f.id); setTourTouched(true); if (revised) applyTourDates(f.id); }} />
      ))}
    </View>
  );

  return (
    <View
      style={{
        width: '100%',
        flex: 1,
        height: '100%',
        minHeight: '100dvh',
        flexDirection: 'column',
        backgroundColor: bg,
        borderLeftWidth: 1,
        borderLeftColor: border,
        overflow: 'hidden',
        position: 'relative',
        ...(Platform.OS === 'web'
          ? { boxShadow: '-2px 0 12px rgba(0,0,0,0.18)' }
          : {
              shadowColor: '#000',
              shadowOffset: { width: -2, height: 0 },
              shadowOpacity: 0.18,
              shadowRadius: 10,
              elevation: 8,
            }),
      }}
    >
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 18, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: border, gap: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <View style={{ width: 32, height: 32, borderRadius: 10, backgroundColor: '#2ecc71', alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 16, fontWeight: '800', color: '#fff' }}>$</Text>
          </View>
          <Text style={{ fontSize: 17, fontWeight: '700', color: textPrimary, letterSpacing: 0.2 }}>Expense Tracker</Text>
        </View>
        <TouchableOpacity
          onPress={closeRail}
          accessibilityLabel="Close Expense Tracker"
          style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: surface, alignItems: 'center', justifyContent: 'center' }}
        >
          <Ionicons name="close" size={18} color={textSecondary} />
        </TouchableOpacity>
      </View>

      {/* Tab bar */}
      <View style={{ flexDirection: 'row', paddingHorizontal: 18, paddingTop: 14, paddingBottom: 4, gap: 8 }}>
        {[{ id: 'overview', label: 'Overview' }, { id: 'add', label: editingId ? 'Edit Entry' : '+ Add Entry' }].map(t => (
          <TouchableOpacity
            key={t.id}
            onPress={() => goTab(t.id)}
            style={{ paddingVertical: 8, paddingHorizontal: 18, borderRadius: 20, backgroundColor: tab === t.id ? '#2ecc71' : surface }}
          >
            <Text style={{ fontSize: 13, fontWeight: '700', color: tab === t.id ? '#fff' : textSecondary }}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {notice ? (
        <TouchableOpacity onPress={() => setNotice('')} style={{ marginHorizontal: 18, marginTop: 10, padding: 10, borderRadius: 10, backgroundColor: 'rgba(230,126,34,0.14)', borderWidth: 1, borderColor: 'rgba(230,126,34,0.4)' }}>
          <Text style={{ fontSize: 12, color: '#e67e22', fontWeight: '600' }}>{notice} (tap to dismiss)</Text>
        </TouchableOpacity>
      ) : null}

      <ScrollView style={{ flex: 1, minHeight: 0 }} contentContainerStyle={{ padding: 18, paddingTop: 14, paddingBottom: 28, gap: 14 }} showsVerticalScrollIndicator={false}>

        {/* ── OVERVIEW TAB ── */}
        {tab === 'overview' && (
          <>
            {revised && tourFilter}

            {/* Net summary card */}
            <View style={{ backgroundColor: net >= 0 ? 'rgba(46,204,113,0.12)' : 'rgba(231,76,60,0.12)', borderRadius: 14, padding: 16, borderWidth: 1, borderColor: net >= 0 ? 'rgba(46,204,113,0.3)' : 'rgba(231,76,60,0.3)' }}>
              <Text style={{ fontSize: 11, fontWeight: '700', color: textSecondary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>Net</Text>
              <Text style={{ fontSize: 28, fontWeight: '800', color: net >= 0 ? '#2ecc71' : '#e74c3c' }}>
                {net >= 0 ? '+' : '-'}${Math.abs(net).toFixed(2)}
              </Text>
              <View style={{ flexDirection: 'row', gap: 24, marginTop: 8 }}>
                <View>
                  <Text style={{ fontSize: 10, color: textSecondary, fontWeight: '600', textTransform: 'uppercase' }}>Income</Text>
                  <Text style={{ fontSize: 15, fontWeight: '700', color: '#2ecc71' }}>${totals.income.toFixed(2)}</Text>
                </View>
                <View>
                  <Text style={{ fontSize: 10, color: textSecondary, fontWeight: '600', textTransform: 'uppercase' }}>Expenses</Text>
                  <Text style={{ fontSize: 15, fontWeight: '700', color: '#e74c3c' }}>${totals.expenses.toFixed(2)}</Text>
                </View>
              </View>
            </View>

            {/* Tour filter + view switch (revised: the tour picker sits at the top instead) */}
            {!revised && tourFilter}
            <View style={{ flexDirection: 'row', backgroundColor: surface, borderRadius: 12, padding: 3 }}>
              {[{ id: 'category', label: 'By category' }, { id: 'day', label: 'By day' }].map(v => (
                <TouchableOpacity key={v.id} onPress={() => setView(v.id)}
                  style={{ flex: 1, paddingVertical: 8, borderRadius: 10, alignItems: 'center', backgroundColor: view === v.id ? bg : 'transparent' }}>
                  <Text style={{ fontSize: 13, fontWeight: '700', color: view === v.id ? textPrimary : textSecondary }}>{v.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Merch summary: hard, soft, entire */}
            {merchTotals.count > 0 && (
              <Card ui={ui} style={{ gap: 4 }}>
                <Label ui={ui} style={{ marginBottom: 4 }}>Merch</Label>
                <MoneyLine ui={ui} label="Hard merch gross" value={usd(merchTotals.hardGross)} />
                <MoneyLine ui={ui} label="Soft merch gross" value={usd(merchTotals.softGross)} />
                <MoneyLine ui={ui} bold label="Entire gross" value={usd(merchTotals.gross)} />
                <View style={{ height: 1, backgroundColor: border, marginVertical: 6 }} />
                <MoneyLine ui={ui} label="Production cost" value={usd(merchTotals.production)} />
                <MoneyLine ui={ui} label="Seller fee" value={usd(merchTotals.fee)} />
                <MoneyLine ui={ui} bold label="Venue cut" value={usd(merchTotals.venueCut)} color="#6c5ce7" />
                <MoneyLine ui={ui} bold label="Band net" value={usd(merchTotals.net)} color="#2ecc71" />
              </Card>
            )}

            {/* Merch by band (only once a band is named) */}
            {merchBands.some(g => g.band !== 'No band set') && (
              <Card ui={ui} style={{ gap: 0 }}>
                <Label ui={ui} style={{ marginBottom: 6 }}>Merch by band</Label>
                {merchBands.map((g, i) => (
                  <View key={g.band} style={{ paddingVertical: 8, borderTopWidth: i ? 1 : 0, borderTopColor: border }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <Text style={{ fontSize: 14, fontWeight: '600', color: g.band === 'No band set' ? textSecondary : textPrimary }}>{g.band}</Text>
                      <Text style={{ fontSize: 14, fontWeight: '700', color: '#2ecc71' }}>{usd(g.net)}</Text>
                    </View>
                    <Text style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                      Hard {usd(g.hardGross)} · Soft {usd(g.softGross)} · Entire {usd(g.gross)} · Venue cut {usd(g.venueCut)}
                    </Text>
                  </View>
                ))}
              </Card>
            )}

            {loading ? (
              <ActivityIndicator color="#2ecc71" style={{ marginTop: 16 }} />
            ) : filtered.length === 0 && subheaders.length === 0 ? (
              <View style={{ alignItems: 'center', paddingVertical: 32 }}>
                <Ionicons name="receipt-outline" size={32} color={textSecondary} />
                <Text style={{ fontSize: 14, color: textSecondary, marginTop: 10 }}>No entries yet</Text>
                <TouchableOpacity onPress={() => goTab('add')} style={{ marginTop: 12, paddingVertical: 10, paddingHorizontal: 22, backgroundColor: '#2ecc71', borderRadius: 20 }}>
                  <Text style={{ fontSize: 13, fontWeight: '700', color: '#fff' }}>Add your first entry</Text>
                </TouchableOpacity>
              </View>
            ) : view === 'category' ? (
              <>
                {sectionTitle('Expenses')}
                {renderGroups(expenseGroups, 'No expenses yet.', 'expense')}
                {revised ? (
                  <View style={{ flexDirection: 'row' }}>
                    <InlinePillAdd ui={ui} label="New expense category" placeholder="e.g. Sound Gear" onAdd={(n) => addCustomCategory('expense', n, { select: false })} />
                  </View>
                ) : null}
                {sectionTitle('Income')}
                {renderGroups(incomeGroups, 'No income yet.', 'income')}
                {revised ? (
                  <View style={{ flexDirection: 'row' }}>
                    <InlinePillAdd ui={ui} label="New income category" placeholder="e.g. Sponsorship" onAdd={(n) => addCustomCategory('income', n, { select: false })} />
                  </View>
                ) : null}
              </>
            ) : (
              <>
              {/* Revised: By day cards carry the tour's Show / Travel / Off colors, as in the calendar */}
              {revised && viewedTourKinds ? <DayKindLegend ui={ui} colors={laneColors} /> : null}
              {dayGroups.map(day => {
                const dayKind = revised && viewedTourKinds ? viewedTourKinds[toDayKey(day.date)] || null : null;
                const dayColor = dayKind ? laneColors[dayKind] : null;
                return (
                <Card key={day.date} ui={ui} accent={dayColor || undefined}
                  style={[{ gap: 0 }, dayColor ? { backgroundColor: hexWithAlpha(dayColor, 0.08) } : null]}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: 4 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8, flexShrink: 1 }}>
                      <Text style={{ fontSize: 15, fontWeight: '800', color: textPrimary }}>{formatDay(day.date)}</Text>
                      {revised && viewedTourKinds ? (
                        <Text style={{ fontSize: 11, fontWeight: '700', color: dayKind ? textPrimary : textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {dayKind ? DAY_KIND_NAMES[dayKind] : 'Not a tour day'}
                        </Text>
                      ) : null}
                    </View>
                    <Text style={{ fontSize: 12, fontWeight: '700' }}>
                      {day.income > 0 ? <Text style={{ color: '#2ecc71' }}>+{usd(day.income)} </Text> : null}
                      {day.expenses > 0 ? <Text style={{ color: '#e74c3c' }}>−{usd(day.expenses)}</Text> : null}
                    </Text>
                  </View>
                  {day.items.map(it => {
                    const row = { kind: 'single', key: it.id, first: it, items: [it], dayCount: 1 };
                    return (
                      <EntryRow
                        key={it.id} ui={ui} row={row} showCategory
                        expanded={!!expanded[it.id]}
                        onToggle={() => setExpanded((p) => ({ ...p, [it.id]: !p[it.id] }))}
                        onEdit={() => startEdit(it)}
                        onMove={() => setMoving(it)}
                        onDelete={() => requestDelete(it, false)}
                        onEditOccurrence={startEdit}
                        onDeleteOccurrence={(o) => requestDelete(o, false)}
                      />
                    );
                  })}
                </Card>
                );
              })}
              </>
            )}
          </>
        )}

        {/* ── ADD ENTRY TAB ── */}
        {tab === 'add' && (
          <>
            {/* Revised: which tour this goes to comes first (it used to sit at the bottom, so entries landed in All Tours). */}
            {revised && userFolders.length > 0 ? (
              <Card ui={ui} style={{ padding: 12, gap: 8 }}>
                <Label ui={ui} style={strongLabel}>Tour</Label>
                {tourChips}
              </Card>
            ) : null}

            {/* Day selector: compact and always first */}
            <Card ui={ui} style={{ padding: 10, gap: 8 }}>
              {showPeriod ? (
                selectedTour && tourDays.length ? (
                  <View style={{ gap: 4 }}>
                    <Label ui={ui}>Per-day items run</Label>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: textPrimary }}>
                      Every day of {selectedTour.name}
                    </Text>
                    <Text style={{ fontSize: 12, color: textSecondary }}>
                      {formatDay(tourDays[0])} to {formatDay(tourDays[tourDays.length - 1])} · {tourDays.length} {tourDays.length === 1 ? 'day' : 'days'}{tourDays.length >= 400 ? ' (limited to 400)' : ''}
                      {perDayTotal > 0 ? ` · ${usd(perDayTotal)} total` : ''}
                    </Text>
                  </View>
                ) : (
                  <>
                    <Label ui={ui}>Per-day items run</Label>
                    <DateField ui={ui} label="From" value={rangeStart} onChange={(v) => { setRangeStart(v); if (v > rangeEnd) setRangeEnd(v); }} />
                    <DateField ui={ui} label="To" value={rangeEnd} onChange={(v) => { setRangeEnd(v); if (v < rangeStart) setRangeStart(v); }} />
                    <Text style={{ fontSize: 12, color: textSecondary }}>
                      {periodDays.length} {periodDays.length === 1 ? 'day' : 'days'}
                      {perDayTotal > 0 ? ` · ${usd(perDayTotal)} total` : ''}
                      {periodDays.length >= 400 ? ' · limited to 400 days' : ''}
                      {selectedTour ? ' · This tour has no dates yet, so pick the period.' : ' · Link a tour below to use its days instead.'}
                    </Text>
                  </>
                )
              ) : null}
              {showDate ? (
                <>
                  <DateField ui={ui} label={editingSeries ? 'Day' : 'Date'} value={entryDate} onChange={setEntryDate}
                    accent={revised && selectedTour ? (laneColors[(selectedTour.kinds || {})[toDayKey(entryDate)]] || null) : null} />
                  {revised && selectedTour && tourDays.length ? (
                    <View style={{ gap: 8 }}>
                      <TourDayStrip ui={ui} days={tourDays} kinds={selectedTour.kinds || {}} colors={laneColors} value={entryDate} onPick={setEntryDate} />
                      <DayKindLegend ui={ui} colors={laneColors} />
                      {!(selectedTour.kinds || {})[toDayKey(entryDate)] ? (
                        <Text style={{ fontSize: 12, color: ui.textSecondary }}>This date isn't on {selectedTour.name}.</Text>
                      ) : null}
                    </View>
                  ) : null}
                  {editingSeries ? (
                    <Text style={{ fontSize: 12, color: textSecondary }}>
                      The date only changes this one day. Choosing “This and future” or “All” keeps each day on its own date.
                    </Text>
                  ) : null}
                </>
              ) : null}
            </Card>

            {/* Expense / Income toggle */}
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {[{ id: 'expense', label: 'Expense' }, { id: 'income', label: 'Income' }].map(t => (
                <TouchableOpacity
                  key={t.id}
                  onPress={() => {
                    if (editingId) return;
                    setEntryType(t.id);
                    setCategory(revised ? '' : (t.id === 'expense' ? 'crew' : 'guarantee'));
                    setSubheaderId('');
                  }}
                  style={{ flex: 1, paddingVertical: 12, borderRadius: 12, alignItems: 'center', backgroundColor: entryType === t.id ? (t.id === 'income' ? '#2ecc71' : '#e74c3c') : surface, borderWidth: 1, borderColor: border, opacity: editingId && entryType !== t.id ? 0.5 : 1 }}
                >
                  <Text style={{ fontWeight: '800', fontSize: 14, color: entryType === t.id ? '#fff' : textSecondary }}>{t.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Category + subheader */}
            <Card ui={ui}>
              <View style={{ gap: 8 }}>
                <Label ui={ui} style={revised ? strongLabel : null}>Category</Label>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {[...(entryType === 'expense' ? EXPENSE_CATEGORIES : INCOME_CATEGORIES), ...(revised ? customCategories[entryType] || [] : [])].map(cat => (
                    <View key={cat.id} style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Pill ui={ui} small label={cat.label} on={category === cat.id}
                      onPress={() => {
                        if (editingId && (cat.id === 'merch') !== (category === 'merch')) return;
                        // Revised: Merch is one page, the same from Expense or Income.
                        if (revised && cat.id === 'merch_net') {
                          if (editingId) return;
                          setEntryType('expense'); setCategory('merch'); setSubheaderId('');
                          return;
                        }
                        setCategory(cat.id); setSubheaderId('');
                      }} />
                    {revised && !editingId && (customCategories[entryType] || []).some((c) => c.id === cat.id) ? (
                      <TouchableOpacity onPress={() => requestDeleteCategory(entryType, cat)} accessibilityLabel={`Delete the ${cat.label} category`} style={{ padding: 4, marginLeft: 2 }}>
                        <Ionicons name="close-circle-outline" size={16} color="rgba(231,76,60,0.8)" />
                      </TouchableOpacity>
                    ) : null}
                    </View>
                  ))}
                  {revised && !editingId ? (
                    <InlinePillAdd ui={ui} label="New category" placeholder={entryType === 'income' ? 'e.g. Sponsorship' : 'e.g. Sound Gear'}
                      onAdd={(n) => addCustomCategory(entryType, n)} />
                  ) : null}
                </View>
              </View>
              {revised && !category ? null : (
              <View style={{ gap: 8 }}>
                <Label ui={ui} style={revised ? strongLabel : null}>Subheader (optional)</Label>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {!revised ? <Pill ui={ui} small label="None" on={!subheaderId} onPress={() => setSubheaderId('')} /> : null}
                  {subheadersFor(subheaders, category).map(s => (
                    <Pill key={s.id} ui={ui} small label={s.name} on={subheaderId === s.id}
                      onPress={() => setSubheaderId(revised && subheaderId === s.id ? '' : s.id)} />
                  ))}
                  <InlinePillAdd ui={ui} label="New subheader" placeholder="Subheader name"
                    onAdd={(n) => { const id = createSubheader(category, n); if (id) setSubheaderId(id); }} />
                </View>
              </View>
              )}
            </Card>

            {isMerchForm ? (
              <>
                {/* Money header: live totals */}
                <View style={{ backgroundColor: 'rgba(46,204,113,0.1)', borderRadius: 14, padding: 14, borderWidth: 1, borderColor: 'rgba(46,204,113,0.3)', gap: 2 }}>
                  <Label ui={ui} style={{ marginBottom: 6 }}>{revised ? 'Merch: one page for income and expenses' : 'Merch money'}</Label>
                  <MoneyLine ui={ui} label="Hard merch gross" value={usd(liveMerch.hardGross)} />
                  <MoneyLine ui={ui} label="Soft merch gross" value={usd(liveMerch.softGross)} />
                  <MoneyLine ui={ui} bold label="Entire gross" value={usd(liveMerch.gross)} />
                  <View style={{ height: 1, backgroundColor: border, marginVertical: 6 }} />
                  <MoneyLine ui={ui} label="Production cost" value={usd(liveMerch.production)} />
                  <MoneyLine ui={ui} label="Seller fee" value={usd(liveMerch.fee)} />
                  <MoneyLine ui={ui} bold label="Venue cut" value={usd(liveMerch.venueCut)} color="#6c5ce7" />
                  <MoneyLine ui={ui} bold label="Band net" value={usd(liveMerch.net)} color="#2ecc71" />
                  {liveMerch.production > 0 ? (
                    <MoneyLine ui={ui} label="After production cost" value={usd(liveMerch.profit)} color={liveMerch.profit >= 0 ? '#2ecc71' : '#e74c3c'} />
                  ) : null}
                </View>

                {/* 1. Production cost first, as line items */}
                <Card ui={ui}>
                  <Label ui={ui}>Merch production cost</Label>
                  <ProductionLines ui={ui} lines={productionLines} onChange={setProductionLines} />
                </Card>

                {/* 2. Seller fee with a descriptor */}
                <Card ui={ui}>
                  <Label ui={ui}>Seller Fee</Label>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                    {[{ id: 'flat', label: 'Flat $' }, { id: 'pct', label: '% of sales' }, { id: 'hourly', label: 'Hourly' }]
                      .filter(t => !(revised && t.id === 'pct' && sellerFeeType !== 'pct')).map(t => (
                      <Pill key={t.id} ui={ui} small label={t.label} on={sellerFeeType === t.id} onPress={() => setSellerFeeType(t.id)} />
                    ))}
                  </View>
                  {sellerFeeType === 'hourly' ? (
                    <View style={{ flexDirection: 'row', gap: 10 }}>
                      <View style={{ flex: 1, gap: 6 }}>
                        <Label ui={ui}>Rate per hour $</Label>
                        <Field ui={ui} numeric value={sellerFee} onChangeText={setSellerFee} placeholder="0.00" />
                      </View>
                      <View style={{ flex: 1, gap: 6 }}>
                        <Label ui={ui}>Hours</Label>
                        <Field ui={ui} numeric value={sellerFeeHours} onChangeText={setSellerFeeHours} placeholder="0" />
                      </View>
                    </View>
                  ) : (
                    <Field ui={ui} numeric value={sellerFee} onChangeText={setSellerFee} placeholder={sellerFeeType === 'pct' ? 'Percentage' : 'Amount'} />
                  )}
                  {sellerFeeType !== 'flat' && liveMerch.fee > 0 ? (
                    <Text style={{ fontSize: 12, color: textSecondary }}>
                      {sellerFeeType === 'pct' ? `${toNum(sellerFee)}% of ${usd(liveMerch.gross)}` : `${usd(toNum(sellerFee))} x ${liveMerch.hours || 1} h`} = {usd(liveMerch.fee)}
                    </Text>
                  ) : null}
                  <Field ui={ui} value={sellerFeeNote} onChangeText={setSellerFeeNote} placeholder="What the fee is for (e.g. 1 venue seller, 4 hours)" />
                </Card>

                {/* 3. Gross sales (revised: moved to the end as income, below) */}
                {!revised ? merchGrossCard : null}

                {/* 4. Venue cut section */}
                <Card ui={ui} accent="#6c5ce7" style={{ gap: 14 }}>
                  <View>
                    <Text style={{ fontSize: 15, fontWeight: '800', color: textPrimary }}>Venue cut</Text>
                    <Text style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
                      Pick the split, band/venue. 80/20 means the band keeps 80 and the venue keeps 20. Use Custom for anything else.
                    </Text>
                  </View>
                  <SplitPicker ui={ui} label="Hard merch split" value={hardBandPct} onChange={setHardBandPct}
                    customOpen={hardCustomOpen} onCustomOpenChange={setHardCustomOpen} fallback={MERCH_DEFAULT_HARD_BAND_PCT} />
                  <SplitPicker ui={ui} label="Soft merch split" value={softBandPct} onChange={setSoftBandPct}
                    customOpen={softCustomOpen} onCustomOpenChange={setSoftCustomOpen} fallback={MERCH_DEFAULT_SOFT_BAND_PCT} />
                  <Text style={{ fontSize: 13, fontWeight: '700', color: '#6c5ce7' }}>Venue keeps {usd(liveMerch.venueCut)}</Text>
                </Card>

                {/* 5. Details */}
                <Card ui={ui}>
                  <View style={{ gap: 6 }}>
                    <Label ui={ui}>Name (optional)</Label>
                    <Field ui={ui} value={name} onChangeText={setName} placeholder="e.g. Denver show merch" />
                  </View>
                  <View style={{ gap: 8 }}>
                    <Label ui={ui}>Band (optional)</Label>
                    {bandNames.length > 0 && (
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                        {bandNames.map(n => (
                          <Pill key={n} ui={ui} small label={n} on={band.trim() === n} onPress={() => setBand(band.trim() === n ? '' : n)} />
                        ))}
                      </View>
                    )}
                    <Field ui={ui} value={band} onChangeText={setBand} placeholder="Which band's merch is this?" />
                  </View>
                  <View style={{ gap: 6 }}>
                    <Label ui={ui}>Notes / description (optional)</Label>
                    <Field ui={ui} multiline value={description} onChangeText={setDescription} placeholder="How was this calculated? e.g. half PayPal, half cash" />
                  </View>
                </Card>

                {/* Revised: income comes last on the merch page */}
                {revised ? merchGrossCard : null}
              </>
            ) : (
              <>
                {lines.map((l, i) => (
                  <View key={l.id} style={{ gap: 10 }}>
                    <Card ui={ui}>
                      {lines.length > 1 ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Label ui={ui}>Line item {i + 1}</Label>
                          {!editingId ? (
                            <TouchableOpacity onPress={() => removeLine(l.id)} accessibilityLabel={`Remove line item ${i + 1}`} style={{ padding: 4 }}>
                              <Ionicons name="trash-outline" size={16} color="rgba(231,76,60,0.75)" />
                            </TouchableOpacity>
                          ) : null}
                        </View>
                      ) : null}
                      <View style={{ gap: 6 }}>
                        <Label ui={ui}>Item name</Label>
                        <Field ui={ui} value={l.name} onChangeText={(t) => patchLine(l.id, { name: t })} placeholder={entryType === 'income' ? 'e.g. Door deal, Guarantee' : 'e.g. Van rental, Hotel, Gas'} />
                      </View>
                      <View style={{ gap: 8 }}>
                        <Label ui={ui}>How is it counted?</Label>
                        <UnitFields
                          ui={ui}
                          modes={editingId ? UNIT_MODES.filter((u) => (editingSeries ? u.id === 'per_day' : u.id !== 'per_day')) : UNIT_MODES}
                          mode={l.unitMode} onMode={(m) => patchLine(l.id, { unitMode: m })}
                          amount={l.amount} onAmount={(v) => patchLine(l.id, { amount: v })}
                          unitCount={l.unitCount} onUnitCount={(v) => patchLine(l.id, { unitCount: v })}
                          unitPrice={l.unitPrice} onUnitPrice={(v) => patchLine(l.id, { unitPrice: v })}
                        />
                      </View>
                      {l.descOpen || l.description ? (
                        <View style={{ gap: 6 }}>
                          <Label ui={ui}>Description (optional)</Label>
                          <Field ui={ui} multiline value={l.description} onChangeText={(t) => patchLine(l.id, { description: t })} placeholder="Anything worth remembering about this line" />
                        </View>
                      ) : (
                        <TouchableOpacity onPress={() => patchLine(l.id, { descOpen: true })}>
                          <Text style={{ fontSize: 13, fontWeight: '700', color: '#6c5ce7' }}>+ Add description</Text>
                        </TouchableOpacity>
                      )}
                    </Card>
                  </View>
                ))}
                {!editingId ? <AddRowButton ui={ui} label="Add line item" onPress={addLine} /> : null}
              </>
            )}

            {/* Which tour */}
            {!revised && userFolders.length > 0 ? (
              <Card ui={ui}>
              {userFolders.length > 0 && (
                <View style={{ gap: 8 }}>
                  <Label ui={ui}>Link to tour (optional)</Label>
                  {tourChips}
                </View>
              )}
              </Card>
            ) : null}
          </>
        )}
      </ScrollView>

      {/* Pinned Save bar */}
      {tab === 'add' && (
        <View style={{ paddingHorizontal: 18, paddingTop: 12, paddingBottom: 16, borderTopWidth: 1, borderTopColor: border, backgroundColor: bg, gap: 8 }}>
          {formError ? <Text style={{ fontSize: 13, color: '#e74c3c', fontWeight: '600' }}>{formError}</Text> : null}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
            <View>
              <Text style={[ui.label, { fontSize: 10 }]}>{editingSeries ? 'This item, all days' : (editingId ? 'This entry now' : 'This entry')}</Text>
              <Text style={{ fontSize: 14, fontWeight: '700', color: saveTotals.entry.net === 0 ? textSecondary : (saveTotals.entry.net > 0 ? '#2ecc71' : '#e74c3c') }}>
                {signedUsd(saveTotals.entry.net)}
              </Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={[ui.label, { fontSize: 10 }]}>{revised ? `Net after saving · ${tourName(linkedTourId || 'none')}` : 'Net after saving · all tours'}</Text>
              <Text style={{ fontSize: 18, fontWeight: '800', color: saveTotals.after.net >= 0 ? '#2ecc71' : '#e74c3c' }}>
                {signedUsd(saveTotals.after.net)}
              </Text>
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            {editingId ? (
              <TouchableOpacity onPress={cancelEdit} style={{ paddingVertical: 14, paddingHorizontal: 20, borderRadius: 13, alignItems: 'center', backgroundColor: surface, borderWidth: 1, borderColor: border }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: textSecondary }}>Cancel</Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              onPress={handleSave}
              disabled={saving}
              style={{ flex: 1, paddingVertical: 14, backgroundColor: '#2ecc71', borderRadius: 13, alignItems: 'center', opacity: saving ? 0.7 : 1 }}
            >
              {saving
                ? <ActivityIndicator color="#fff" />
                : <Text style={{ fontSize: 15, fontWeight: '800', color: '#fff', letterSpacing: 0.3 }}>{editingId ? 'Save Changes' : (!isMerchForm && activeLines().length > 1 ? `Save ${activeLines().length} Entries` : 'Save Entry')}</Text>
              }
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Dialogs */}
      {dialog && dialog.kind === 'delete' ? (
        <ChoiceDialog
          ui={ui}
          title={dialog.wholeSeries ? 'Delete this per-day item?' : 'Delete which occurrences?'}
          body={dialog.wholeSeries
            ? 'This removes every day of the item.'
            : `“${entryTitle(dialog.entry)}” repeats every day. Choose what to delete.`}
          options={dialog.wholeSeries
            ? [{ id: 'all', label: 'Delete all occurrences', danger: true }]
            : [
              { id: 'this', label: 'This occurrence', hint: formatDay(dialog.entry.date) },
              { id: 'future', label: 'This and future occurrences', hint: `From ${formatDay(dialog.entry.date, false)} on`, danger: true },
              { id: 'all', label: 'All occurrences', danger: true },
            ]}
          onPick={(scope) => { const d = dialog; setDialog(null); performDelete(d.entry, scope, d.wholeSeries ? d.rowItems : null); }}
          onCancel={() => setDialog(null)}
        />
      ) : null}
      {dialog && dialog.kind === 'edit' ? (
        <ChoiceDialog
          ui={ui}
          title="Apply this change to…"
          body={`“${entryTitle(dialog.entry)}” repeats every day.`}
          options={SCOPES.map((s) => ({
            id: s.id,
            label: s.label,
            hint: s.id === 'this' ? formatDay(dialog.entry.date) : (s.id === 'future' ? `From ${formatDay(dialog.entry.date, false)} on` : 'Every day of the item'),
          }))}
          onPick={(scope) => { setDialog(null); commitSave(scope); }}
          onCancel={() => setDialog(null)}
        />
      ) : null}
      {dialog && dialog.kind === 'delcat' ? (
        <TypeToConfirmDialog
          ui={ui}
          title={`Delete “${dialog.name}”?`}
          body={`This deletes the category and its ${dialog.count} ${dialog.count === 1 ? 'entry' : 'entries'} across all your tours. This can't be undone.`}
          expected={dialog.name}
          confirmLabel={`Delete ${dialog.count} ${dialog.count === 1 ? 'entry' : 'entries'} and the category`}
          busy={deletingCategory}
          onConfirm={() => performDeleteCategory(dialog.type, dialog.id)}
          onCancel={() => setDialog(null)}
        />
      ) : null}

      {dialog && dialog.kind === 'delsub' ? (
        <ChoiceDialog
          ui={ui}
          title={`Delete “${dialog.name}”?`}
          body={dialog.count
            ? `Its ${dialog.count} item${dialog.count === 1 ? '' : 's'} stay in the category, just without a subheader.`
            : 'It has no items.'}
          options={[{ id: 'delete', label: 'Delete subheader', danger: true }]}
          onPick={() => { const d = dialog; setDialog(null); performDeleteSubheader(d.id); }}
          onCancel={() => setDialog(null)}
        />
      ) : null}
      {moving ? (
        <MoveSheet
          ui={ui}
          entry={moving}
          subheaders={subheaders}
          customCategories={customCategories}
          onPick={(cat, sub) => applyMove(moving, cat, sub)}
          onCreate={(cat, n) => { const id = createSubheader(cat, n); if (id) applyMove(moving, cat, id); }}
          onCancel={() => setMoving(null)}
        />
      ) : null}
    </View>
  );
}
